import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./HeaderProfile.module.scss";
import { Popup } from "@progress/kendo-react-popup";
import BellImage from "../../assets/bell-not.svg";
import { Badge, Loader } from "@progress/kendo-react-indicators";
import { getNotifications, updateNotifications } from "api";
import { shortMonths } from "components/MyMatters";
import { Overlay } from "components/Overlay";
import { CrossIcon } from "components/tabs/TabTitle";
import { changeLedgerLink } from "actions/ledgerLinks.action";
import { LuBellRing } from "react-icons/lu";

const formatNotificationTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const isSameCalendarDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const formatNotificationDate = (dateUTC) => {
  if (!dateUTC) return "";
  const date = new Date(dateUTC);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const time = formatNotificationTime(date);

  if (isSameCalendarDay(date, now)) return `Today ${time}`;
  if (isSameCalendarDay(date, yesterday)) return `Yesterday ${time}`;

  return `${date.getDate()} ${shortMonths[date.getMonth()]} ${time}`;
};

const LINK_HANDLERS = {
  OpenCaseFileLedger: (item, { navigate, onNavigate }) => {
    const matterId = item.matterId ?? item.matterID;
    if (!matterId) return;
    navigate(`/matters/${matterId}?tab=ledger`);
    onNavigate?.();
  },
  OpenMatterLedger: (item, { navigate, dispatch, onNavigate }) => {
    const matterId = item.matterId ?? item.matterID;
    const matterRef = item.matterRef;
    if (!matterId || !matterRef) return;
    dispatch(changeLedgerLink("MatterLedger", { matterId, matterRef }));
    navigate("/accounts/matterledger");
    onNavigate?.();
  },
};

const handleNotificationLink = (item, deps) => {
  const handler = item.linkFunction && LINK_HANDLERS[item.linkFunction];
  if (handler) handler(item, deps);
};

const Notifications = () => {
  const notificationRef = React.useRef();
  const [notifications, setNotifications] = useState([]);
  const [showPortal, setShowPortal] = useState(false);
  const anchor = React.useRef();
  const [show, setShow] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    getApiNotifications();
  }, []);

  const getApiNotifications = async () => {
    try {
      const notifications = await getNotifications();
      setNotifications(notifications?.data?.data);
    } catch (error) {
      console.error(error);
    }
  };

  const toggle = () => {
    setShow((prev) => !prev);
    if (!show) setShowPortal(true);
  };

  const updateNotificationData = async (item, seen) => {
    try {
      await updateNotifications([item.id], seen);
      await getApiNotifications();
    } catch (error) { }
  };

  const getNotificationsCount = () => {
    const count = notifications.filter((item) => item.seen === false);
    if (count.length >= 10) return "9+";
    return count.length;
  };

  useEffect(() => {
    window.onclick = (event) => {
      if (event.target.id === "overlay") setShow(false);
    };
  }, []);

  const linkDeps = {
    navigate,
    dispatch,
    onNavigate: () => setShow(false),
  };

  return (
    <>
      <div onClick={toggle}>
        <div
          className="position-relative cursor-pointer d-flex align-items-center justify-content-center"
          ref={anchor}
          style={{ width: "40px", height: "40px", zIndex: 20 }}
        >
          {!show && (
            <div className={`${styles["notifications-border"]} p-2`}>
              <img src={BellImage} alt="" />
              {!!getNotificationsCount() && (
                <Badge className={`${styles["notification-badge"]} ms-2`}>
                  <span>{getNotificationsCount()}</span>
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {show && (
        <Overlay hideOverlay={() => setShowPortal(false)}>
          {/*Pass any data to the modal */}
        </Overlay>
      )
      }
      <div className={`${styles["notification-popup"]}`} ref={notificationRef}>
        <Popup
          show={show}
          popupClass={styles["notification-content"]}
          animate={false}
          anchor={anchor.current}
        >
          <div className="popup-items mb-2">
            <div className="w-100">
              <div
                className={`p-4 d-flex align-items-center justify-space-between w-100`}
              >
                <p className="mb-0 fw-semibold">Notifications</p>
                <CrossIcon onClick={() => setShow(false)} selected />
              </div>
              {/* </div> */}
              <div className="w-100 border-bottom border-opacity-50"></div>
            </div>
            <div
              className={`container-fluid ${styles["not-container"]}`}
              role="button"
            >
              {notifications.map((item, index) => (
                <NotificationsItem
                  item={item}
                  key={item.id}
                  updateNotification={updateNotificationData}
                  onLinkClick={() => handleNotificationLink(item, linkDeps)}
                />
              ))}
            </div>
          </div>
        </Popup>
      </div>
    </>
  );
};

export default Notifications;

const NotificationsItem = ({ item, updateNotification, onLinkClick }) => {
  const { seen, details, dateUTC, matterRef, linkFunction } = item;
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  const isLinkableMatter =
    matterRef && linkFunction && LINK_HANDLERS[linkFunction];

  const handleMatterClick = (e) => {
    e.stopPropagation();
    if (isLinkableMatter) onLinkClick();
  };

  const handleToggleSeen = async (e) => {
    e.stopPropagation();
    if (isMarkingRead) return;
    setIsMarkingRead(true);
    try {
      await updateNotification(item, seen ? false : undefined);
    } finally {
      setIsMarkingRead(false);
    }
  };

  return (
    <div
      className={`d-flex gap-2 ps-2 py-2 align-items-center  justify-content-between ${styles["notification-item"]}`}
    >
      <div style={{ flex: 1 }}>
        <p className={`${styles["notification-details"]} mb-1 fs-sm ${!seen ? "fw-bold" : "fw-normal"
          }`}>{details}</p>
        {matterRef && (
          <p className={`${styles["notification-matter"]} mb-1`}>
            Matter:{" "}
            {isLinkableMatter ? (
              <button
                type="button"
                className={styles["notification-matter-link"]}
                onClick={handleMatterClick}
              >
                {matterRef}
              </button>
            ) : (
              <span>{matterRef}</span>
            )}
          </p>
        )}
        <p className={`${styles["notification-date"]} mb-0`}>
          {formatNotificationDate(dateUTC)}
        </p>
      </div>
      <button
        type="button"
        className={`${styles["notification-action"]} ${seen ? styles["notification-action-read"] : ""}`}
        onClick={handleToggleSeen}
        disabled={isMarkingRead}
        aria-label={isMarkingRead ? "Processing" : seen ? "Mark as unread" : "Mark as read"}
      >
        {isMarkingRead ? (
          <Loader size="small" type="infinite-spinner" />
        ) : (
          <>
            <LuBellRing size={18} />
            <span>{seen ? "Mark as unread" : "Mark as read"}</span>
          </>
        )}
      </button>
    </div >
  );
};
